<script setup lang="ts">
/**
 * UiKitAlert — алерт HeroUI UI-kit.
 * Цвета берутся ТОЛЬКО из токенов (классы hui-*). Хардкод hex запрещён.
 * Figma: 3128-454.
 */
type HuiColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
type HuiVariant = 'solid' | 'flat' | 'faded' | 'bordered'
type HuiRadius = 'none' | 'sm' | 'md' | 'lg' | 'full'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    variant?: HuiVariant
    radius?: HuiRadius
    title?: string
    description?: string
    hideIcon?: boolean
    closable?: boolean
  }>(),
  {
    color: 'default',
    variant: 'flat',
    radius: 'md',
    title: '',
    description: '',
    hideIcon: false,
    closable: false,
  },
)

defineEmits<{ (e: 'close'): void }>()

defineOptions({ name: 'UiKitAlert' })

const BASE = 'relative w-full flex items-start gap-3 p-4'

// Контейнер задаёт фон/границу И цвет текста (заголовок наследует, описание — через opacity)
const CONTAINER: Record<HuiVariant, Record<HuiColor, string>> = {
  solid: {
    default: 'bg-hui-default text-hui-default-foreground',
    primary: 'bg-hui-primary text-hui-primary-foreground',
    secondary: 'bg-hui-secondary text-hui-secondary-foreground',
    success: 'bg-hui-success text-hui-success-foreground',
    warning: 'bg-hui-warning text-hui-warning-foreground',
    danger: 'bg-hui-danger text-hui-danger-foreground',
  },
  flat: {
    default: 'bg-hui-default-100 border border-hui-default-200 text-hui-foreground',
    primary: 'bg-hui-primary/10 border border-hui-primary/20 text-hui-primary-700',
    secondary: 'bg-hui-secondary/10 border border-hui-secondary/20 text-hui-secondary-700',
    success: 'bg-hui-success/10 border border-hui-success/20 text-hui-success-700',
    warning: 'bg-hui-warning/10 border border-hui-warning/20 text-hui-warning-700',
    danger: 'bg-hui-danger/10 border border-hui-danger/20 text-hui-danger-700',
  },
  faded: {
    default: 'bg-hui-default-100 border border-hui-default-200 text-hui-foreground',
    primary: 'bg-hui-default-100 border border-hui-default-200 text-hui-primary-700',
    secondary: 'bg-hui-default-100 border border-hui-default-200 text-hui-secondary-700',
    success: 'bg-hui-default-100 border border-hui-default-200 text-hui-success-700',
    warning: 'bg-hui-default-100 border border-hui-default-200 text-hui-warning-700',
    danger: 'bg-hui-default-100 border border-hui-default-200 text-hui-danger-700',
  },
  bordered: {
    default: 'bg-transparent border border-hui-default text-hui-foreground',
    primary: 'bg-transparent border border-hui-primary text-hui-primary-700',
    secondary: 'bg-transparent border border-hui-secondary text-hui-secondary-700',
    success: 'bg-transparent border border-hui-success text-hui-success-700',
    warning: 'bg-transparent border border-hui-warning text-hui-warning-700',
    danger: 'bg-transparent border border-hui-danger text-hui-danger-700',
  },
}

// Иконка в solid — полупрозрачный кружок на foreground; иначе — сплошной цветной кружок
const ICON_SOLID: Record<HuiColor, string> = {
  default: 'bg-hui-default-foreground/20 text-hui-default-foreground',
  primary: 'bg-hui-primary-foreground/20 text-hui-primary-foreground',
  secondary: 'bg-hui-secondary-foreground/20 text-hui-secondary-foreground',
  success: 'bg-hui-success-foreground/20 text-hui-success-foreground',
  warning: 'bg-hui-warning-foreground/20 text-hui-warning-foreground',
  danger: 'bg-hui-danger-foreground/20 text-hui-danger-foreground',
}
const ICON_COLOR: Record<HuiColor, string> = {
  default: 'bg-hui-default text-hui-default-foreground',
  primary: 'bg-hui-primary text-hui-primary-foreground',
  secondary: 'bg-hui-secondary text-hui-secondary-foreground',
  success: 'bg-hui-success text-hui-success-foreground',
  warning: 'bg-hui-warning text-hui-warning-foreground',
  danger: 'bg-hui-danger text-hui-danger-foreground',
}

const RADIUS: Record<HuiRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-hui-sm',
  md: 'rounded-hui-md',
  lg: 'rounded-hui-lg',
  full: 'rounded-3xl',
}

const containerClass = computed(() => [BASE, CONTAINER[props.variant][props.color], RADIUS[props.radius]])
const iconClass = computed(() => props.variant === 'solid' ? ICON_SOLID[props.color] : ICON_COLOR[props.color])
// info — default/primary/secondary, остальные — свой глиф
const iconKey = computed<'info' | 'success' | 'warning' | 'danger'>(() => {
  if (props.color === 'success') return 'success'
  if (props.color === 'warning') return 'warning'
  if (props.color === 'danger') return 'danger'
  return 'info'
})
</script>

<template>
  <div :class="containerClass" role="alert">
    <span
      v-if="!hideIcon"
      class="flex-none flex items-center justify-center w-6 h-6 mt-0.5 rounded-full"
      :class="iconClass"
    >
      <slot name="icon">
        <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            v-if="iconKey === 'info'"
            fill-rule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM9 9a1 1 0 0 1 2 0v4a1 1 0 1 1-2 0V9zm1-4.25A1.25 1.25 0 1 0 10 7.25 1.25 1.25 0 0 0 10 4.75z"
            clip-rule="evenodd"
          />
          <path
            v-else-if="iconKey === 'success'"
            fill-rule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.7-9.3a1 1 0 0 0-1.4-1.4L9 10.58l-1.3-1.3a1 1 0 0 0-1.4 1.42l2 2a1 1 0 0 0 1.4 0l4-4z"
            clip-rule="evenodd"
          />
          <path
            v-else-if="iconKey === 'warning'"
            fill-rule="evenodd"
            d="M8.26 3.1c.77-1.33 2.71-1.33 3.48 0l6.04 10.45c.77 1.34-.2 3-1.74 3H3.96c-1.54 0-2.5-1.66-1.74-3L8.26 3.1zM10 7a1 1 0 0 0-1 1v3a1 1 0 1 0 2 0V8a1 1 0 0 0-1-1zm0 7.5a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z"
            clip-rule="evenodd"
          />
          <path
            v-else
            fill-rule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM10 5a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1zm0 9.5a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z"
            clip-rule="evenodd"
          />
        </svg>
      </slot>
    </span>

    <div class="min-w-0 flex-1">
      <p v-if="title || $slots.title" class="text-sm font-medium leading-5">
        <slot name="title">{{ title }}</slot>
      </p>
      <p
        v-if="description || $slots.default"
        class="text-[0.8125rem] leading-5 opacity-80"
        :class="{ 'mt-0.5': title || $slots.title }"
      >
        <slot>{{ description }}</slot>
      </p>
      <div v-if="$slots.actions" class="mt-3 flex items-center gap-2">
        <slot name="actions" />
      </div>
    </div>

    <button
      v-if="closable"
      type="button"
      class="flex-none -mr-1 -mt-1 inline-flex items-center justify-center w-7 h-7 rounded-hui-sm opacity-70 hover:opacity-100 transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-hui-focus"
      aria-label="Закрыть"
      @click="$emit('close')"
    >
      <svg class="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M6 6l8 8M14 6l-8 8" stroke-linecap="round" />
      </svg>
    </button>
  </div>
</template>
